// TraineeResume — Phase 7. Digital learning resume + native share + push to Loyalty.
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Share2, Send, Award, Trophy, BookOpen, Sparkles, Download } from 'lucide-react';

export default function TraineeResume() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get('/me/resume').then(r => setData(r.data)).catch(() => setData(null)); }, []);

    const share = async () => {
        if (!data) return;
        const text = `${data.user.full_name} · ${data.total_points} pts · ${data.certificates.length} certificates · ${data.badges.length} badges. Powered by Propulse Cables & Wires Learning Academy.`;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'My Learning Resume', text, url: window.location.href });
            } else {
                await navigator.clipboard.writeText(text);
                toast.success('Resume snippet copied to clipboard');
            }
        } catch { /* user cancelled */ }
    };

    const pushLoyalty = async () => {
        try {
            await api.post('/me/resume/push-to-loyalty');
            toast.success('Resume snapshot pushed to Loyalty');
        } catch { toast.error('Push failed'); }
    };

    const downloadJSON = () => {
        if (!data) return;
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `learning-resume-${data.user.id}.json`; a.click();
        URL.revokeObjectURL(url);
    };

    if (!data) return <div className="py-12 text-center text-white/60">Loading resume…</div>;

    return (
        <div className="space-y-6" data-testid="trainee-resume-page">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-32 h-32 bg-amber-400/20 blur-3xl rounded-full" />
                <div className="relative">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Digital Resume</div>
                    <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-1">{data.user.full_name}</h1>
                    <div className="text-sm text-slate-300 mt-1">{data.user.role} · {data.user.region || 'India'} · #{data.rank} on the leaderboard</div>
                    <div className="grid grid-cols-3 gap-3 mt-5 max-w-sm">
                        <Stat icon={Sparkles} value={data.total_points} label="Points" />
                        <Stat icon={Trophy} value={data.certificates.length} label="Certificates" />
                        <Stat icon={Award} value={data.badges.length} label="Badges" />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-5">
                        <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-bold" data-testid="resume-share-btn"><Share2 className="w-4 h-4" /> Share</button>
                        <button onClick={pushLoyalty} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-white text-sm font-bold" data-testid="resume-push-btn"><Send className="w-4 h-4" /> Push to Loyalty</button>
                        <button onClick={downloadJSON} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-white text-sm font-bold" data-testid="resume-download-btn"><Download className="w-4 h-4" /> Download</button>
                    </div>
                </div>
            </div>

            <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                <h2 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Completed Courses ({data.completed_courses.length})</h2>
                <div className="space-y-1">
                    {data.completed_courses.map(c => (
                        <div key={c.id} className="text-sm ring-1 ring-slate-100 rounded p-2 flex items-center justify-between">
                            <span className="font-semibold">{c.title}</span>
                            <span className="text-xs text-slate-500">{c.category_label || c.category}</span>
                        </div>
                    ))}
                    {data.completed_courses.length === 0 && <div className="text-xs text-slate-400 italic">Finish your first course to populate this section.</div>}
                </div>
            </section>

            <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                <h2 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><Trophy className="w-4 h-4" /> Certificates ({data.certificates.length})</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                    {data.certificates.map(c => (
                        <div key={c.id} className="rounded-xl ring-1 ring-amber-200 bg-amber-50 p-3">
                            <div className="font-bold text-sm">{c.course_title || 'Certificate'}</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Issued · {new Date(c.issued_at).toLocaleDateString()}</div>
                        </div>
                    ))}
                    {data.certificates.length === 0 && <div className="text-xs text-slate-400 italic col-span-2">No certificates yet.</div>}
                </div>
            </section>

            <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                <h2 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><Award className="w-4 h-4" /> Badges ({data.badges.length})</h2>
                <div className="flex flex-wrap gap-2">
                    {data.badges.map(b => (
                        <div key={b.code} className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold ring-1 ring-amber-200">
                            {b.name || 'Badge'}
                        </div>
                    ))}
                    {data.badges.length === 0 && <div className="text-xs text-slate-400 italic">Earn badges to fill this row.</div>}
                </div>
            </section>
        </div>
    );
}

function Stat({ icon: Icon, value, label }) {
    return (
        <div className="bg-white/10 backdrop-blur rounded-xl px-3 py-2 text-center">
            <Icon className="w-4 h-4 mx-auto text-amber-300" />
            <div className="text-xl font-extrabold mt-0.5">{value}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
        </div>
    );
}
